import React, { useState } from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  useInternalNode,
  useReactFlow,
} from "@xyflow/react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";

// components
import { TooltipWithIconCustom } from "../../ui/tooltip-custom";
import { LoaderMUI } from "../../../assets/global/loader/loader-mui";
import { SelectConnectionType } from "../../ui/dropdown-connection-type";
import { DeleteConfirmationDialog } from "../../ui/confirm-delete";

// services
import { useUserContext } from "../../../services/context/user-context";
import { axiosInstance } from "../../../services/api/api";
import { usePlannerContext } from "../../../services/context/planner-context";
import { getEdgeParams } from "./adge-position";

// constants
import { TEXT } from "../../../constants/text";

//icons
import { ReactComponent as IconEdit } from "../../../assets/svg/edit.svg";
import { ReactComponent as IconDelete } from "../../../assets/svg/delete.svg";
import { ReactComponent as IconCheck } from "../../../assets/svg/check.svg";
import { ReactComponent as IconRoad } from "../../../assets/svg/road.svg";
import { ReactComponent as IconStreet } from "../../../assets/svg/street.svg";
import { ReactComponent as IconPedestrian } from "../../../assets/svg/pedestrian.svg";

import "./buttonedge.css";
import { COLORS_GLOBAL } from "../../../constants/colors.constants";


export function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  source,
  target,
  style = {},
  markerEnd,
  data,
  databaseId,
}) {
  const { viewOnly } = useUserContext();
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const { edges, setEdges, connectionTypes } = usePlannerContext();
  const [showControls, setShowControls] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!sourceNode || !targetNode) {
    return null;
  }

  const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(
    sourceNode,
    targetNode,
    data.connectionType
  );

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sourcePos,
    targetPosition: targetPos,
    targetX: tx,
    targetY: ty,
  });

  /*  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  }); */

  /*   const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  }); */

  // for hover styling

  const currentConnectionType = connectionTypes.find(
    (c) => c.edge_name === data.connectionType
  );
  const typeColor = currentConnectionType.color;

  const icon = {
    road: <IconStreet width={"24px"} height={"24px"} />,
    adjacent: <IconStreet width={"24px"} height={"24px"} />,
    pedestrian: <IconPedestrian width={"24px"} height={"24px"} />,
    truckroute: <IconRoad width={"24px"} height={"24px"} />,
  };

  const deleteConnection = async (connectionId) => {
    try {
      const response = await axiosInstance.delete(
        `/connections/${connectionId}`
      );

      setEdges((edges) => edges.filter((edge) => edge.id !== id));
    } catch (error) {
      // Handle error
      /* setErrorDelete(
          error.response ? error.response.data.message : error.message
        ); */
    } finally {
      setLoading(false);
    }
  };

  const onEdgeDelete = () => {
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  };

  const handleOpenDialog = () => {
    if (id) {
      setIsDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleDelete = () => {
    if (id) {
      setLoading(true);
      deleteConnection(id);
      setIsDialogOpen(false);
    }
  };

  const tooltips = {
    adjacent: TEXT.planner.connections.adjacentTooltip,
    road: "",
    truckroute: "",
    pedestrian: "",
  };

  return (
    <>
      {/* Loader */}
      {loading && (
        <Box sx={{ width: "100%", height: "80px", position: "relative" }}>
          <LoaderMUI withBackdrop={false} />
        </Box>
      )}
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: typeColor,
          strokeWidth: 4,
        }}
        /* selectable={!viewOnly} */
      />
      <EdgeLabelRenderer>
        {/* <div
          className="custom-edge__label nodrag nopan"
          style={{
            position: "absolute",
            transform: `translate(+10%, -50%) translate(${sourceX}px, ${sourceY}px)`,
            pointerEvents: "auto", // so we can hover the label
          }}
        >
          <Typography>{data.label}</Typography>
        </div>
        <div
          className="custom-edge__label nodrag nopan"
          style={{
            position: "absolute",
            transform: `translate(-110%, -50%) translate(${targetX}px, ${targetY}px)`,
            pointerEvents: "auto", // so we can hover the label
          }}
        >
              <Typography color="#fff">{data.label}</Typography>
        </div> */}
        <div
          className="custom-edge__label nodrag nopan"
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: "auto", // so we can hover the label
          }}
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(false)}
        >
          {/* <Tooltip key={data.label} title={data.label} placement="right">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: "#FBFBFD",
                boxShadow: 3,
                borderRadius: "50%",
                overflow: "hidden",
                padding: "8px",
              }}
            >
              {icon[data.connectionType]}
            </Box>
          </Tooltip> */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              background: "#FBFBFD",
              boxShadow: 3,
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: typeColor,
                color: "#fff",
                padding: "8px",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              {tooltips[data.connectionType] ? (
                <TooltipWithIconCustom
                  text={tooltips[data.connectionType]}
                  placement="top"
                  color="#fff"
                >
                  <Typography color="#FBFBFD" fontSize={"14px"}>
                    {data.label}
                  </Typography>
                </TooltipWithIconCustom>
              ) : (
                <Typography color="#FBFBFD" fontSize={"14px"}>
                  {data.label}
                </Typography>
              )}

              {showControls && (
                <Box
                  sx={{
                    display: "flex",
                    ml: "8px",
                    transition: "opacity 0.5s ease",
                    opacity: showControls ? 1 : 0,
                    pointerEvents: showControls ? "auto" : "none",
                  }}
                >
                  <IconButton onClick={handleOpenDialog} disabled={viewOnly}>
                    <IconDelete fill={viewOnly ? COLORS_GLOBAL.BTN_DISABLED : "#fff"} width={"18px"} height={"18px"} />
                  </IconButton>
                </Box>
              )}
            </Box>

            {/*  { showControls && <Box
              sx={{
                padding: "12px",
                transition: 'opacity 0.2s ease',
                opacity: showControls ? 1 : 0,
                pointerEvents: showControls ? 'auto' : 'none',
              }}
            >
              <SelectConnectionType
                connectionId={id}
                value={data.connectionType}
                label={data.name}
              />
            </Box>} */}
          </Box>
        </div>
      </EdgeLabelRenderer>
      <DeleteConfirmationDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onDelete={handleDelete}
        name={data.label}
        type={data.label}
      />
    </>
  );
}
